import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";

const links = [
    { to: "/", label: "Start", id: "start" },
    { to: "/leistungen", label: "Leistungen", id: "leistungen" },
    { to: "/handyreparatur", label: "Handyreparatur", id: "handyreparatur" },
    { to: "/kontakt", label: "Kontakt", id: "kontakt" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-paper/90 backdrop-blur-xl border-b border-ink/10"
                    : "bg-paper/60 backdrop-blur-md border-b border-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 h-[92px] flex items-center justify-between">
                <Link to="/" data-testid="navbar-logo" className="group">
                    <Logo size="md" variant="header" />
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.id}`}
                            className={({ isActive }) =>
                                `text-[13px] font-medium tracking-wide uppercase transition-colors link-underline ${
                                    isActive ? "text-brand" : "text-ink/80 hover:text-ink"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href="tel:+499414610027"
                        data-testid="navbar-call-button"
                        className="hidden md:inline-flex items-center gap-2 bg-brand text-paper px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-brandDark transition-colors duration-300"
                    >
                        <Phone size={14} />
                        <span>0941 461 0027</span>
                    </a>
                    <a
                        href="https://wa.me/4917620366268"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="navbar-whatsapp-button"
                        className="hidden md:inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[#1DA851] transition-colors duration-300"
                    >
                        <MessageCircle size={14} />
                        <span>WhatsApp</span>
                    </a>
                    <button
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Menü"
                        data-testid="navbar-mobile-toggle"
                        className="md:hidden p-2 text-ink"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="md:hidden bg-paper border-t border-ink/10" data-testid="navbar-mobile-menu">
                    <div className="px-6 py-8 flex flex-col gap-5">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `font-display text-3xl tracking-tight ${isActive ? "text-brand" : "text-ink"}`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <a
                            href="tel:+499414610027"
                            className="mt-4 inline-flex items-center justify-center gap-2 bg-brand text-paper px-5 py-3 text-xs tracking-[0.2em] uppercase font-semibold"
                        >
                            <Phone size={14} /> 0941 461 0027
                        </a>
                        <a
                            href="https://wa.me/4917620366268"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-5 py-3 text-xs tracking-[0.2em] uppercase font-semibold"
                        >
                            <MessageCircle size={14} /> WhatsApp
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
